import { useEffect } from 'react';
import { Outlet, NavLink, useLocation, useResolvedPath } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SettingsProvider, useSettings } from './context/SettingsContext.jsx';
import { setAuthErrorHandler } from './api/client.js';
import AuthBanner from './components/AuthBanner.jsx';
import { NAV_SECTIONS, VerificationBaseProvider, activeNavItem, verificationPath } from './navigation.jsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retrying an unreachable service only doubles the time before the user sees the problem.
      retry: (failureCount, error) => !error?.isUnreachable && !error?.isAuthError && failureCount < 1,
      refetchOnWindowFocus: false,
    },
  },
});

const [PRIMARY_SECTION] = NAV_SECTIONS;

function AuthErrorBridge() {
  const { triggerAuthBanner } = useSettings();

  useEffect(() => {
    setAuthErrorHandler(triggerAuthBanner);
    return () => setAuthErrorHandler(null);
  }, [triggerAuthBanner]);

  return null;
}

/**
 * Wrapper for the verification module when it is mounted inside the production dashboard, which
 * already provides the sidebar and AppBar. It supplies the module's data/settings providers plus
 * its own sub-navigation, page heading and connection state -- the pieces the standalone AppShell
 * would otherwise provide.
 *
 * `base` is detected from the route this layout is mounted on, so the module can be moved to a
 * different URL without touching a single link inside it. Pass it explicitly only to override.
 */
export default function VerificationLayout({ base }) {
  const resolved = useResolvedPath('.');
  const moduleBase = base || resolved.pathname;

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <VerificationBaseProvider base={moduleBase}>
          <AuthErrorBridge />
          <ModuleFrame base={moduleBase} />
        </VerificationBaseProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
}

function ModuleFrame({ base }) {
  const location = useLocation();
  const active = activeNavItem(base, location.pathname);
  const activeSegment = active?.segment ?? '';
  const primarySegments = PRIMARY_SECTION.items.map((item) => item.segment);
  const tabValue = primarySegments.includes(activeSegment) ? activeSegment : false;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
      >
        <Stack spacing={0.25}>
          <Typography variant="h5" fontWeight={800}>
            {active?.title || 'Email health'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {active?.caption || 'Check contacts before you email them.'}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 0, md: 2 }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs value={tabValue} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 44 }}>
          {PRIMARY_SECTION.items.map(({ segment, label, icon: Icon }) => (
            <Tab
              key={segment || 'index'}
              value={segment}
              label={label}
              icon={<Icon fontSize="small" />}
              iconPosition="start"
              component={NavLink}
              to={verificationPath(base, segment)}
              end={segment === ''}
              sx={{ minHeight: 44 }}
            />
          ))}
        </Tabs>

      </Stack>

      <Box sx={{ pt: 3 }}>
        <AuthBanner />
        <Outlet />
      </Box>
    </Box>
  );
}
